import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Mail, Phone, Calendar, Shield, Users, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { getAvatarColor, getInitials } from "@/lib/utils";

interface TeamMemberProfileModalProps {
  member: any | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (memberId: string) => void;
  onSendMessage?: (memberId: string) => void;
  onScheduleOneOnOne?: (memberId: string) => void;
}

export function TeamMemberProfileModal({
  member,
  isOpen,
  onClose,
  onEdit,
  onSendMessage,
  onScheduleOneOnOne,
}: TeamMemberProfileModalProps) {
  if (!member) {
    return null;
  }

  const fullName = `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim() || member.email;
  const status = member.status || (member.isInvitation ? "pending" : member.isActive ? "active" : "inactive");
  const department = member.department || member.user?.department;
  const role = member.organizationRole || member.role || "staff";
  const joinedAt = member.joinedAt || member.user?.joinedAt;
  const expiresAt = member.invitationExpiresAt;

  const formatDate = (value?: string | Date | null) => {
    if (!value) return null;
    const parsed = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return format(parsed, 'MMM dd, yyyy');
  };

  const getStatusBadgeVariant = () => {
    switch (status) {
      case "active":
        return "default" as const;
      case "pending":
        return "outline" as const;
      default:
        return "secondary" as const;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl font-semibold">Team Member Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border">
                <AvatarImage src={member.avatar || member.user?.avatar || undefined} alt={fullName} />
                <AvatarFallback className={`font-semibold text-lg ${getAvatarColor(member.firstName, member.lastName)}`}>
                  {getInitials(member.firstName ?? member.user?.firstName, member.lastName ?? member.user?.lastName)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-xl font-semibold text-foreground">{fullName}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Badge variant="secondary" className="capitalize">
                    {role}
                  </Badge>
                  <Badge variant={getStatusBadgeVariant()} className="capitalize">
                    {status}
                  </Badge>
                  {department && <Badge variant="outline">{department}</Badge>}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {onSendMessage && (
                <Button variant="outline" size="sm" onClick={() => onSendMessage?.(member.id)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Message
                </Button>
              )}
              {onScheduleOneOnOne && (
                <Button variant="outline" size="sm" onClick={() => onScheduleOneOnOne?.(member.id)}>
                  <Users className="h-4 w-4 mr-2" />
                  Schedule 1:1
                </Button>
              )}
              {onEdit && !member.isInvitation && (
                <Button size="sm" onClick={() => onEdit?.(member.id)}>
                  <Shield className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Contact</h3>
                <div className="mt-2 space-y-2 text-sm">
                  <p className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{member.email || member.user?.email || 'Not provided'}</span>
                  </p>
                  {(member.phone || member.user?.phone) && (
                    <p className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{member.phone || member.user?.phone}</span>
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Timeline</h3>
                <div className="mt-2 space-y-2 text-sm">
                  {formatDate(joinedAt) && (
                    <p className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>Joined {formatDate(joinedAt)}</span>
                    </p>
                  )}
                  {member.isInvitation && formatDate(expiresAt) && (
                    <p className="flex items-center gap-2 text-amber-600">
                      <ArrowRight className="h-4 w-4" />
                      <span>Invitation expires {formatDate(expiresAt)}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Summary</h3>
                <div className="mt-2 space-y-2 text-sm text-muted-foreground">
                  {member.jobTitle && (
                    <p>
                      <span className="font-medium text-foreground">Job Title:</span> {member.jobTitle}
                    </p>
                  )}
                  {member.manager?.name && (
                    <p>
                      <span className="font-medium text-foreground">Manager:</span> {member.manager.name}
                    </p>
                  )}
                  {!member.isInvitation && (
                    <p>
                      <span className="font-medium text-foreground">Status:</span> {status}
                    </p>
                  )}
                  {member.isInvitation && (
                    <p>
                      <span className="font-medium text-foreground">Invitation Status:</span> Pending acceptance
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TeamMemberProfileModal;
